import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Text } from '@mantine/core';
import { IconShoppingCart } from '@tabler/icons-react';
import { CartStore } from './CartStore';
import { CartController } from './CartController';
import { CartItem } from './CartItem';
import { PaymentMethods } from './PaymentMethods';
import { CheckoutButton } from './CheckoutButton';
import { THEME_COLORS } from '../../theme/ThemeConstants';

interface DesktopCartViewProps {
  cartStore: CartStore;
  controller: CartController;
  animatingItems: Map<string, 'add' | 'remove'>;
  animationDuration: { ADD: number; REMOVE: number };
  onRemoveItem: (lineId: string) => void;
}

export const DesktopCartView: React.FC<DesktopCartViewProps> = observer(({
  cartStore,
  controller,
  animatingItems,
  animationDuration,
  onRemoveItem
}) => {
  return (
    <Box
      style={{
        backgroundColor: THEME_COLORS.background.tertiary,
        borderRadius: 8,
        border: `1px solid ${THEME_COLORS.border.secondary}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 400,
        position: 'sticky',
        bottom: 16,
        marginTop: 'auto'
      }}
    >
      <Box style={{ padding: 16 }}>
        <Text size='lg' fw={700} c={THEME_COLORS.text.primary} mb={16}>
          Cart Items
        </Text>

        {cartStore.isEmpty ? (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 32,
              textAlign: 'center',
              minHeight: 200
            }}
          >
            <IconShoppingCart size={48} color={THEME_COLORS.text.dimmed} style={{ marginBottom: 16 }} />
            <Text size='sm' c={THEME_COLORS.text.secondary}>
              To complete your purchase, add items to your cart.
            </Text>
          </Box>
        ) : (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              maxHeight: 300,
              overflowY: 'auto',
              paddingBottom: 12,
              scrollbarWidth: 'thin',
              scrollbarColor: `${THEME_COLORS.border.primary} transparent`
            }}
          >
            {cartStore.items.map((item) => {
              const isItemLoading = cartStore.isLoading && cartStore.loadingLineId === item.id;
              const isUpdateLoading = isItemLoading && cartStore.loadingOperation === 'update';
              const animationType = animatingItems.get(item.id);

              return (
                <CartItem
                  key={item.id}
                  item={item}
                  isLoading={cartStore.isLoading}
                  isUpdateLoading={isUpdateLoading}
                  isRemoving={animationType === 'remove'}
                  animationType={animationType}
                  animationDuration={animationDuration[animationType === 'add' ? 'ADD' : 'REMOVE']}
                  onUpdateQuantity={(lineId, quantity) => controller.updateQuantity(lineId, quantity)}
                  onRemove={onRemoveItem}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        style={{
          marginTop: 'auto',
          borderTop: `1px solid ${THEME_COLORS.border.primary}`,
          padding: 16
        }}
      >
        <CheckoutButton
          isEmpty={cartStore.isEmpty}
          isLoading={cartStore.isLoading}
          total={cartStore.total}
          currency={cartStore.currency}
          itemCount={cartStore.items.length}
          onClick={() => controller.handleCheckout()}
          fullWidth
        />

        <PaymentMethods />
      </Box>
    </Box>
  );
});
